import styled from '@emotion/styled';
import { StyledButton } from '@selfserve/react-ui';
import { PricingTier, BillingCycle } from '../data/interfaces/pricing-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { Tooltip } from '@kep-ui-kit/ui-components';
import { useTranslation } from 'react-i18next';
import {
  ApplicationFeatures,
  ButtonType,
  trackButtonClickEvent,
} from '@selfserve/react-utils';
import { storageKeys } from '@selfserve/react-hooks';

const StyledSecondaryButton = styled(StyledButton)`
  background: #ebebeb;
  color: #333;

  &:hover {
    background: #d9d9d9;
  }

  /* Override loader color to match text color for accessibility */
  .MuiCircularProgress-root,
  [class*='CircularProgress'],
  svg[class*='circular'] {
    color: #333;
  }

  /* Also target the SVG circle element directly */
  svg circle {
    stroke: #333;
  }
`;

const StyledCard = styled.div<{ highlighted: boolean }>`
  display: flex;
  flex-direction: column;
  width: 290px;
  height: 820px;
  border: ${({ highlighted }) =>
    highlighted ? '2px solid #006efa' : '1px solid #d9d9d9'};
  border-radius: 8px;
  overflow: hidden;
  background: white;
`;

const StyledBestValueBanner = styled.div<{ visible: boolean }>`
  background: #006efa;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  width: 100%;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
`;

const StyledBannerText = styled.p`
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 21px;
  color: white;
  margin: 0;
  text-align: center;
  white-space: nowrap;
`;

const StyledCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  padding: 16px;
  flex: 1;
`;

const StyledTierName = styled.p<{ color: string }>`
  font-family: 'Centra No1';
  font-weight: 700;
  font-size: 32px;
  line-height: 32px;
  color: ${({ color }) => color};
  text-align: center;
  margin: 0;
  width: 100%;
`;

const StyledDescription = styled.p`
  font-family: 'Centra No1';
  font-weight: 500;
  font-size: 16px;
  line-height: 21px;
  color: #333;
  text-align: center;
  margin: 0;
  height: 63px;
  width: 100%;
`;

const StyledPrice = styled.p`
  font-family: 'Centra No1';
  font-weight: 700;
  font-size: 48px;
  line-height: 50px;
  color: #333;
  text-align: center;
  margin: 0;
  width: 100%;
  min-height: 75px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledPricePerMonth = styled.span`
  font-family: 'Centra No1';
  font-weight: 700;
  font-size: 48px;
  line-height: 50px;
  color: #333;
  display: flex;
  align-items: baseline;
`;

const StyledPricePerYear = styled.span`
  font-family: 'Centra No1';
  font-weight: 500;
  font-size: 16px;
  line-height: 21px;
  color: #878787;
`;

const StyledFeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const StyledFeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: 'Centra No1';
  font-weight: 500;
  font-size: 16px;
  line-height: 21px;
  color: #333;
`;

const StyledFeatureText = styled.span``;

interface PricingCardProps {
  tier: PricingTier;
  billingCycle: BillingCycle;
  isCurrent: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
}

const getDisplayPrice = (
  tier: PricingTier,
  cycle: BillingCycle
): JSX.Element | string => {
  if (tier.useDisplayPrice) {
    return tier.displayPrice;
  }

  if (cycle === BillingCycle.MONTHLY) {
    const price = tier.monthlyPrice;
    return `$${Math.round(price).toLocaleString()}`;
  }

  // Annual view: show monthly equivalent + annual total
  const monthlyEquivalent = Math.round(tier.annualPrice / 12);
  const annualTotal = Math.round(tier.annualPrice);

  // Free tier should show simple format in both views
  if (annualTotal === 0) {
    return '$0';
  }

  return (
    <>
      <StyledPricePerMonth>
        ${monthlyEquivalent.toLocaleString()}
        <StyledPricePerYear as="span" style={{ marginLeft: '8px' }}>
          /month
        </StyledPricePerYear>
      </StyledPricePerMonth>
      <StyledPricePerYear>
        ${annualTotal.toLocaleString()} /year
      </StyledPricePerYear>
    </>
  );
};

const API_URL = process.env['NX_API_URL'];

export const PricingCard = ({
  tier,
  billingCycle,
  isCurrent,
  disabled = false,
  disabledTooltip,
}: PricingCardProps) => {
  const [busy, setBusy] = useState(false);
  const purchaseUrl = useMemo(
    () => `${API_URL}/purchase-manager/purchase-manager/${tier.id}`,
    []
  );
  const { t } = useTranslation();

  const handleButtonClick = useCallback(() => {
    trackButtonClickEvent(
      ButtonType.Launch,
      'plan_cta_click',
      tier.name,
      null,
      ApplicationFeatures.SUBSCRIPTION_FLOW
    );

    const target =
      sessionStorage.getItem(storageKeys.IS_IN_APP_CONTEXT) === 'true' &&
      !tier.buttonUrl
        ? '_self'
        : '_blank';
    const url = tier.buttonUrl || purchaseUrl;

    if (target === '_self') {
      setBusy(true);
    }

    window.open(url, target);
  }, [purchaseUrl, tier.buttonUrl]);

  useEffect(() => {
    const handlePageShow = (event: any) => {
      // event.persisted is true if the page is restored from bfcache - need to reset busy state
      if (event.persisted) {
        setBusy(false);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const getButtonText = useCallback(() => {
    return isCurrent ? t('pricing-page.current-plan') : tier.buttonText;
  }, [isCurrent, tier.buttonText]);

  const sharedButtonProps = useMemo(() => {
    return {
      size: 'large' as const,
      fullWidth: true,
      onClick: () => handleButtonClick(),
      withoutMargin: true,
      loading: busy,
      disabled: isCurrent || disabled,
      style: {
        width: 'auto',
        padding: '16px',
        height: '48px',
      },
    };
  }, [busy, isCurrent, disabled]);

  return (
    <StyledCard highlighted={tier.highlighted}>
      <StyledBestValueBanner visible={tier.highlighted}>
        <StyledBannerText>Best value</StyledBannerText>
      </StyledBestValueBanner>
      <StyledCardContent>
        <StyledTierName color={tier.color}>{tier.name}</StyledTierName>
        <StyledDescription>{tier.description}</StyledDescription>
        <StyledPrice>{getDisplayPrice(tier, billingCycle)}</StyledPrice>
        {/* Tooltip is attached to a wrapping span - not fire mouse events  */}
        <Tooltip
          title={
            !isCurrent && disabled && disabledTooltip ? disabledTooltip : ''
          }
        >
          <span
            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            {tier.buttonVariant === 'primary' ? (
              <StyledButton {...sharedButtonProps} color="primary">
                {getButtonText()}
              </StyledButton>
            ) : (
              <StyledSecondaryButton {...sharedButtonProps}>
                {getButtonText()}
              </StyledSecondaryButton>
            )}
          </span>
        </Tooltip>
        <StyledFeaturesList>
          {tier.features.map((feature, index) => (
            <StyledFeatureItem key={index}>
              <StyledFeatureText>{feature.text}</StyledFeatureText>
              {feature.tooltip && (
                <Tooltip title={feature.tooltip}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '16px',
                      height: '16px',
                    }}
                  >
                    <InfoIcon />
                  </Box>
                </Tooltip>
              )}
            </StyledFeatureItem>
          ))}
        </StyledFeaturesList>
      </StyledCardContent>
    </StyledCard>
  );
};

const InfoIcon = () => {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6 0C9.31371 2.89694e-07 12 2.68629 12 6C12 9.31371 9.31371 12 6 12C2.68629 12 1.95766e-08 9.31371 0 6C2.89694e-07 2.68629 2.68629 1.95762e-08 6 0ZM6 6C5.44772 6 5 6.44772 5 7V9C5 9.55228 5.44772 10 6 10C6.55228 10 7 9.55228 7 9V7C7 6.44772 6.55228 6 6 6ZM6 3C5.44772 3 5 3.44772 5 4C5 4.55228 5.44772 5 6 5C6.55228 5 7 4.55228 7 4C7 3.44772 6.55228 3 6 3Z"
        fill="#333333"
      />
    </svg>
  );
};
